import {
  AppSubscriptionProduct,
  ServerPlanType,
  useFetchAppSubscriptionProducts,
  useFetchPlanTypes,
} from '@expane/data'
import { CloseButton, Dialog, Modal, Spinner, usePopupOpenState } from '@expane/ui'
import { FC } from 'react'
import { Control, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MainInfoFormValues } from '@expane/logic/business'
import { normalizeAppSubscriptionProducts } from '@expane/logic/appSubscription'
import { AppSubscriptionProductCard } from '@expane/widgets'

export const useOpenAppSubscriptionProductsDialog = (control: Control<MainInfoFormValues>) => {
  const { isOpen, openPopup, closePopup } = usePopupOpenState()

  const appSubscriptionProductsDialog = isOpen ? (
    <AppSubscriptionProductsDialog control={control} closeDialog={closePopup} />
  ) : null

  return { openAppSubscriptionProductsDialog: openPopup, appSubscriptionProductsDialog }
}

interface AppSubscriptionProductsDialogProps {
  control: Control<MainInfoFormValues>
  closeDialog: () => void
}
const AppSubscriptionProductsDialog: FC<AppSubscriptionProductsDialogProps> = ({
  control,
  closeDialog,
}) => {
  const { data: planTypes, isLoading: isLoadingPlanTypes } = useFetchPlanTypes()
  const { data: appSubscriptionProducts, isLoading: isLoadingAppSubscriptionProducts } =
    useFetchAppSubscriptionProducts()

  const isLoading = isLoadingPlanTypes || isLoadingAppSubscriptionProducts

  const products = normalizeAppSubscriptionProducts(appSubscriptionProducts?.products)

  if (isLoading)
    return (
      <Modal>
        <Spinner expandCentered />
      </Modal>
    )

  if (!products || !planTypes) return null

  return (
    <AppSubscriptionProductsDialogLogic
      products={products}
      planTypes={planTypes}
      control={control}
      closeDialog={closeDialog}
    />
  )
}

const AppSubscriptionProductsDialogLogic: FC<
  AppSubscriptionProductsDialogProps & {
    products: AppSubscriptionProduct[]
    planTypes: ServerPlanType[]
  }
> = ({ products, planTypes, control, closeDialog }) => {
  const { t } = useTranslation()

  return (
    <Modal close={closeDialog}>
      <Dialog>
        <Dialog.Title>{t('billingInfo')}</Dialog.Title>

        <Dialog.Body className="w-192">
          <Controller
            control={control}
            name="planType"
            render={({ field: { value, onChange } }) => (
              <div className="flex w-full gap-6">
                {products.map(product => (
                  <AppSubscriptionProductCard
                    key={product.id}
                    planTypes={planTypes}
                    product={product}
                    activePlanType={value}
                    onPlanTypeSelected={planType => onChange(planType)}
                  />
                ))}
              </div>
            )}
          />
        </Dialog.Body>
        <Dialog.Footer>
          <CloseButton onClick={closeDialog} />
        </Dialog.Footer>
      </Dialog>
    </Modal>
  )
}
